import { createSlice } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { HomeObjectType } from "./HomeObjectType";
import { BiomarkerGroupedData } from "../../../common/entities/type";
import { ReactNode } from "react";
import { dataPlatformServerObjectType } from "../../../providers/dataPlatformReportTypes";

export interface RootStatePlatform {
  platform: RootStatePlatformObj;
}

export interface RootStatePlatformObj {
  // From the GetAllBiomarkers catalog. `qualitative`/`qoptions` are present in
  // the payload for qualitative biomarkers (used to look them up by `id`).
  biomarkerList: {
    id: string;
    name: string;
    text: ReactNode;
    qualitative?: boolean;
    qoptions?: string[];
  }[];
  logicCategories: {
    metrics: {
      id: string;
      type: number;
      label: string;
      unit: string;
    }[];
    biomarkers: {
      id: string;
      name: string;
      shortName: string | null;
      label: string;
      unit: string;
      testKind: number;
      metricType: number;
    }[];
  } | null;
  dataNutServer: HomeObjectType;
  dataPlatformServer: dataPlatformServerObjectType;
  biomarkerData: BiomarkerGroupedData;
  ASActiveDate: string;
  selectedMetric: string;
  selectedSystem: number;
  selectedActiveConnection: number;
  connectionTextExpanded: boolean;
}

export interface RootStatePlatform {
  platform: RootStatePlatformObj;
}

const initialPlatformState: RootStatePlatformObj = {
  biomarkerList: [],
  logicCategories: null,
  dataNutServer: {} as HomeObjectType,
  dataPlatformServer: {
    prescriptionPlan: [],
  } as unknown as dataPlatformServerObjectType,
  biomarkerData: {},
  ASActiveDate: format(new Date().toISOString(), "yyyy-MM-dd"),
  selectedMetric: "",
  selectedSystem: 0,
  selectedActiveConnection: 0,
  connectionTextExpanded: false,
};

const platformSlice = createSlice({
  name: "platform",
  initialState: initialPlatformState,
  reducers: {
    updateBiomarkerList(state, action) {
      state.biomarkerList = action.payload;
    },
    updateLogicCategories(state, action) {
      state.logicCategories = action.payload;
    },
    updateDataNutServer(state, action) {
      state.dataNutServer = action.payload;
    },
    updateDataPlatformServer(state, action) {
      state.dataPlatformServer = action.payload;
    },
    updateBiomarkerData(state, action) {
      state.biomarkerData = action.payload;
    },
    updateASActiveDate(state, action) {
      state.ASActiveDate = action.payload;
    },
    setSelectedMetric(state, action) {
      state.selectedMetric = action.payload;
    },
    setSelectedSystem(state, action) {
      state.selectedSystem = action.payload;
    },
    setSelectedActiveConnection(state, action) {
      state.selectedActiveConnection = action.payload;
    },
    setConnectionTextExpanded(state, action) {
      state.connectionTextExpanded = action.payload;
    },
    clearDataPlatformServer(state) {
      state.dataPlatformServer = {
        prescriptionPlan: [],
      } as unknown as dataPlatformServerObjectType;
    },
    clearPlatformState(state) {
      Object.assign(state, {
        biomarkerList: state.biomarkerList,
        dataNutServer: {},
        dataPlatformServer: {
          prescriptionPlan: [],
        } as unknown as dataPlatformServerObjectType,
        biomarkerData: {},
        ASActiveDate: format(new Date().toISOString(), "yyyy-MM-dd"),
        selectedMetric: "",
        selectedSystem: 0,
        selectedActiveConnection: 0,
        connectionTextExpanded: false,
      });
    },
  },
});

export const platformActions = platformSlice.actions;

export default platformSlice;
